import React, { useState, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAlert } from '../contexts/AlertContext';
import { runtimeEnvironment } from '../config/environment';
import { caseApiService } from '../services/caseApiService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@/icons';

export interface ExportCaseModalProps {
    isOpen: boolean;
    onClose: () => void;
}

/**
 * Modal for exporting a case by ID (Root only). Prompts for case ID, fetches case JSON, downloads as file.
 */
export const ExportCaseModal: React.FC<ExportCaseModalProps> = ({ isOpen, onClose }) => {
    const theme = useTheme();
    const { addAlert } = useAlert();
    const [caseIdInput, setCaseIdInput] = useState('');
    const [exporting, setExporting] = useState(false);
    const mouseDownOutsideRef = useRef<boolean>(false);

    if (!isOpen) return null;

    const handleOverlayMouseDown = (e: React.MouseEvent) => {
        mouseDownOutsideRef.current = e.target === e.currentTarget;
    };

    const handleOverlayMouseUp = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget && mouseDownOutsideRef.current && !exporting) {
            onClose();
        }
        mouseDownOutsideRef.current = false;
    };

    const handleExport = async () => {
        const caseId = caseIdInput.trim();
        if (!caseId) return;
        const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!guidRegex.test(caseId)) {
            addAlert('error', 'Please enter a valid case ID (UUID format).');
            return;
        }
        setExporting(true);
        try {
            const caseData = await caseApiService.getCaseById(caseId);
            const json = JSON.stringify(caseData, null, 2);
            const blob = new Blob([json], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${runtimeEnvironment}_case_${caseId}.json`;
            a.click();
            URL.revokeObjectURL(url);
            onClose();
            addAlert('success', 'Case exported successfully.');
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Export failed';
            addAlert('error', msg);
        } finally {
            setExporting(false);
        }
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
            }}
            onMouseDown={handleOverlayMouseDown}
            onMouseUp={handleOverlayMouseUp}
        >
            <div
                style={{
                    backgroundColor: theme.colors.surface,
                    borderRadius: theme.borderRadius.lg,
                    padding: 0,
                    width: '50vw',
                    maxWidth: '600px',
                    boxShadow: theme.shadows.lg,
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: `${theme.spacing.lg} ${theme.spacing.lg}`,
                        borderBottom: `1px solid ${theme.colors.border.default}`,
                        backgroundColor: theme.colors.primary,
                    }}
                >
                    <h2
                        style={{
                            margin: 0,
                            fontSize: theme.typography.fontSize.xl,
                            fontWeight: theme.typography.fontWeight.semibold,
                            color: theme.colors.text.inverse,
                        }}
                    >
                        Export case
                    </h2>
                    <button
                        type="button"
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: theme.typography.fontSize.xxl,
                            color: theme.colors.text.inverse,
                            cursor: 'pointer',
                            padding: 0,
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: theme.borderRadius.sm,
                        }}
                        onClick={() => !exporting && onClose()}
                        aria-label="Close"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div
                    style={{
                        padding: theme.spacing.lg,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.spacing.lg,
                    }}
                >
                    <label
                        style={{
                            fontSize: theme.typography.fontSize.sm,
                            color: theme.colors.text.secondary,
                            marginBottom: theme.spacing.xs,
                        }}
                    >
                        Case ID
                    </label>
                    <input
                        type="text"
                        value={caseIdInput}
                        onChange={(e) => setCaseIdInput(e.target.value.trim())}
                        placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000"
                        style={{
                            padding: theme.spacing.sm,
                            borderRadius: theme.borderRadius.sm,
                            border: `1px solid ${theme.colors.border.default}`,
                            fontSize: theme.typography.fontSize.md,
                            fontFamily: 'monospace',
                            width: '100%',
                        }}
                        disabled={exporting}
                    />
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: theme.spacing.sm,
                        padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                        borderTop: `1px solid ${theme.colors.border.default}`,
                    }}
                >
                    <button
                        style={{
                            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                            borderRadius: theme.borderRadius.sm,
                            fontSize: theme.typography.fontSize.md,
                            fontWeight: theme.typography.fontWeight.medium,
                            cursor: 'pointer',
                            border: 'none',
                            backgroundColor: theme.colors.surfaceSecondary,
                            color: theme.colors.text.primary,
                        }}
                        onClick={() => !exporting && onClose()}
                        disabled={exporting}
                    >
                        Cancel
                    </button>
                    <button
                        style={{
                            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                            borderRadius: theme.borderRadius.sm,
                            fontSize: theme.typography.fontSize.md,
                            fontWeight: theme.typography.fontWeight.medium,
                            cursor: 'pointer',
                            border: 'none',
                            backgroundColor: theme.colors.primary,
                            color: theme.colors.text.inverse,
                        }}
                        disabled={exporting || !caseIdInput.trim()}
                        onClick={handleExport}
                    >
                        {exporting ? 'Exporting...' : 'Export'}
                    </button>
                </div>
            </div>
        </div>
    );
};
